/**
 * Member workspace (home for paid users). The previous markup
 * burned in 3 hardcoded "Up next" cards (RAG / Zero to First Revenue
 * / Design Systems at Scale) with fake chapter counts and runtimes,
 * plus 3 hardcoded metrics (4.6h / 12 lessons / 65%) and 3 hardcoded
 * hero badges (How LLMs Actually Work / Ch.2 of 8 / 65% complete).
 *
 * Hydration is done by homeMemberHydrator:
 *   /api/topics/recommended?limit=3    — the 3 "Up next" suggestions
 *   /api/user/progress/recent?limit=1  — the hero resume chip
 *   /api/user/learning-stats           — the 3 metrics (this endpoint
 *                                         is implemented server-side;
 *                                         if missing the metrics
 *                                         default to em-dashes)
 *
 * The "Learning desk" kicker and the right-side "Up next" header
 * stay — they are product copy, not data.
 */
export const homeMemberMarkup = String.raw`<!-- Member workspace -->
      <section class="member-command-center home-member-workspace-host" id="memberWorkspaceSection" aria-busy="true">
        <div class="paid-hero-banner">
          <div class="home-member-first-frame__workbench" data-home-first-frame="workbench" aria-hidden="true">
            <div class="home-member-first-frame__main">
              <span class="home-member-first-frame__line home-member-first-frame__line--kicker"></span>
              <span class="home-member-first-frame__line home-member-first-frame__line--title"></span>
              <span class="home-member-first-frame__line home-member-first-frame__line--copy"></span>
              <span class="home-member-first-frame__line home-member-first-frame__line--copy home-member-first-frame__line--copy-short"></span>
              <div class="home-member-first-frame__actions">
                <span class="home-member-first-frame__button"></span>
                <span class="home-member-first-frame__button home-member-first-frame__button--secondary"></span>
              </div>
            </div>
            <div class="home-member-first-frame__up-next">
              <span class="home-member-first-frame__line home-member-first-frame__line--label"></span>
              <span class="home-member-first-frame__up-next-row"></span>
              <span class="home-member-first-frame__up-next-row"></span>
              <span class="home-member-first-frame__up-next-row"></span>
            </div>
            <div class="home-member-first-frame__metrics">
              <span class="home-member-first-frame__metric"></span>
              <span class="home-member-first-frame__metric"></span>
              <span class="home-member-first-frame__metric"></span>
            </div>
          </div>
          <button type="button" class="home-member-first-frame__session-retry" id="homeSessionRetry" hidden>
            We couldn't verify your session. Retry
          </button>
          <div class="paid-hero-main">
            <div class="paid-hero-kicker">Learning desk</div>
            <h1 id="paidHeroTitle">Your learning desk</h1>
            <p id="paidHeroTopicDesc" aria-live="polite"></p>
            <div class="paid-hero-actions">
              <button class="btn btn-primary" id="paidHeroPrimaryAction" data-proto-click="navigateToBrowse('all')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                Browse all topics
              </button>
              <button class="btn btn-outline" id="paidHeroBrowseAction" data-proto-click="showView('ai-create');setActiveTab('ai-create')" style="display:none;">
                AI Create
              </button>
            </div>
            <p id="paidHeroSubtitle" class="paid-hero-chapter-context">Choose a topic to begin.</p>
          </div>
          <div class="paid-hero-side">
            <div class="paid-hero-side-head">
              <span class="paid-hero-side-label" id="upnextCarouselLabel">Up next</span>
              <div class="upnext-dots">
                <span class="upnext-dot active" data-slide-idx="0"></span>
                <span class="upnext-dot" data-slide-idx="1"></span>
              </div>
            </div>
            <div class="upnext-carousel">
              <button class="upnext-nav-btn upnext-nav-prev" id="upnextPrevBtn" aria-label="Previous" type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <button class="upnext-nav-btn upnext-nav-next" id="upnextNextBtn" aria-label="Next" type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M9 18l6-6-6-6"/></svg>
              </button>
              <div class="upnext-slide active" id="paidHeroUpNext">
                <div class="home-upnext-skeleton" aria-hidden="true"></div>
                <div class="home-upnext-skeleton" aria-hidden="true"></div>
                <div class="home-upnext-skeleton" aria-hidden="true"></div>
              </div>
              <div class="upnext-slide" id="paidHeroCloud">
                <div class="upnext-cloud" aria-hidden="true"></div>
              </div>
            </div>
          </div>
          <div class="paid-hero-metrics">
            <div class="paid-hero-metric">
              <div class="paid-hero-metric-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"></circle><path d="M12 7v5l3 2"></path><path d="M4 4l3 3M20 4l-3 3"></path></svg></div>
              <div class="paid-hero-metric-copy"><strong data-bind="home.metricTopicsExplored"><span class="home-metric-skeleton" aria-hidden="true"></span></strong> <span>topics explored</span></div>
            </div>
            <div class="paid-hero-metric">
              <div class="paid-hero-metric-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M4 4v15.5A2.5 2.5 0 0 1 6.5 22H20V6a2 2 0 0 0-2-2H4z"></path><path d="M8 8h8"></path><path d="M8 12h5"></path></svg></div>
              <div class="paid-hero-metric-copy"><strong data-bind="home.metricChaptersDone"><span class="home-metric-skeleton" aria-hidden="true"></span></strong> <span>chapters done</span></div>
            </div>
            <div class="paid-hero-metric">
              <div class="paid-hero-metric-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 9-9"></path><path d="M12 3v9h9"></path><path d="M7 14l2 2 5-6"></path></svg></div>
              <div class="paid-hero-metric-copy"><strong data-bind="home.metricDaysActive"><span class="home-metric-skeleton" aria-hidden="true"></span></strong> <span>days active</span></div>
            </div>
          </div>
        </div>
      </section>

      `;
